import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Lock, UserRound } from 'lucide-react';
import { GoogleIcon, AuthField, errText } from '../components/AuthBits';

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [chooseRoles, setChooseRoles] = useState(null);

  const doLogin = async (role) => {
    setBusy(true);
    try {
      const u = await login(identifier.trim(), password, role);
      toast.success(`Bienvenue ${u.name}`);
      nav(u.role === 'seller' ? '/seller' : '/');
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === 'choose_account') { setChooseRoles(d.roles); return; }
      toast.error(errText(err) || 'Identifiants invalides');
    } finally { setBusy(false); }
  };
  const submit = (e) => { e.preventDefault(); setChooseRoles(null); doLogin(); };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-md mx-auto px-4 py-14">
        <div className="card-elev rounded-3xl p-7">
          <h1 className="font-display text-3xl font-extrabold">{t('login')}</h1>
          <p className="text-slate-400 text-sm mt-1">JOTALY</p>
          <div className="mt-4 p-3 rounded-xl border border-sky-500/30 bg-sky-500/5 text-sky-200 text-xs" data-testid="login-hint">
            Connectez-vous avec votre <strong>e-mail ou numéro de téléphone</strong> + mot de passe, ou en un clic avec <strong>Google</strong>.
          </div>
          <form onSubmit={submit} className="mt-6 space-y-3">
            <AuthField icon={<UserRound className="h-4 w-4" />} type="text" value={identifier} onChange={setIdentifier} placeholder="E-mail ou téléphone (77 123 45 67)" testid="login-email" autoComplete="username" />
            <AuthField icon={<Lock className="h-4 w-4" />} type="password" value={password} onChange={setPassword} placeholder={t('password')} testid="login-password" autoComplete="current-password" />
            <div className="text-right">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-sky-400 hover:underline">Mot de passe oublié ?</Link>
            </div>
            <Button disabled={busy} type="submit" data-testid="login-submit"
              className="w-full btn-brand text-white py-6 rounded-xl font-bold">{t('login')}</Button>
            {chooseRoles && (
              <div className="p-3 rounded-xl border border-amber-500/40 bg-amber-500/5 space-y-2" data-testid="choose-account">
                <div className="text-xs text-amber-200">Deux comptes utilisent cet identifiant. Lequel ouvrir ?</div>
                <div className="grid grid-cols-2 gap-2">
                  {chooseRoles.includes('buyer') && <Button type="button" disabled={busy} data-testid="choose-account-buyer" onClick={() => doLogin('buyer')} variant="outline" className="border-slate-600 bg-transparent text-slate-100">Compte acheteur</Button>}
                  {chooseRoles.includes('seller') && <Button type="button" disabled={busy} data-testid="choose-account-seller" onClick={() => doLogin('seller')} className="btn-brand text-white">Ma boutique</Button>}
                  {chooseRoles.includes('admin') && <Button type="button" disabled={busy} data-testid="choose-account-admin" onClick={() => doLogin('admin')} className="btn-brand text-white">Administration</Button>}
                </div>
              </div>
            )}
          </form>
          <div className="my-5 flex items-center gap-3 text-slate-500 text-xs">
            <div className="flex-1 h-px bg-slate-800" />{t('or')}<div className="flex-1 h-px bg-slate-800" />
          </div>
          <Button data-testid="google-login-btn" onClick={google} variant="outline"
            className="w-full py-6 rounded-xl border-slate-700 hover:bg-slate-800 text-slate-100 bg-transparent">
            <GoogleIcon />{t('google_login')}
          </Button>
          <div className="mt-6 text-center text-sm text-slate-400">
            Pas encore de compte ? <Link to="/register" data-testid="go-register-link" className="text-sky-400 hover:underline">{t('register')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
